// React
import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'

// Libraries
import _get from 'lodash/get'

// Components
import BModalGallery from 'store/modules/product/blocks/ModalGallery'
import Icon from 'ui/components/Icon'
import FocalImage from 'ui/components/FocalImage'

const THUMBNAIL_SIZE = 80

const ModalGallery = (props) => {
  const [swipe, setSwipe] = useState({})
  const [swiping, setSwiping] = useState(false)
  const [galleryOffset, setGalleryOffset] = useState(0)
  const [dimensions, setDimensions] = useState(null)

  useEffect(() => {
    const photoIndex = props.photos.indexOf(props.selectedPhoto)

    if (photoIndex <= -1) {
      setGalleryOffset(0)
    } else {
      setGalleryOffset(Math.min(photoIndex, getMaximumOffset()))
    }
  }, [props.selectedPhoto])

  useEffect(() => {
    const modalContainer = document.getElementById('booqable-quickview-inner')

    if (modalContainer) {
      modalContainer.classList.add('modal-gallery-open')
    }

    return () => {
      if (modalContainer) {
        modalContainer.classList.remove('modal-gallery-open')
      }
    }
  }, [])

  const onTouchStart = (e) => {
    const touch = e.touches[0]

    setSwipe({ x: touch.clientX })
  }

  const onTouchMove = (e) => {
    if (e.changedTouches && e.changedTouches.length) {
      setSwiping(true)
    }
  }

  const onTouchEnd = (e) => {
    const touch = e.changedTouches[0]
    const x = touch.clientX - swipe.x

    if (swiping && x > 50) {
      props.handlePhotoSelect && props.handlePhotoSelect(-1)
    } else if (swiping && x < 50) {
      props.handlePhotoSelect && props.handlePhotoSelect(1)
    }

    setSwiping(false)
  }

  const onImageLoad = ({ target: img }) => {
    setDimensions({ height: img.offsetHeight, width: img.offsetWidth })
  }

  const getMaximumOffset = () => {
    return Math.max(props.photos.length - 1, 0)
  }

  const handleImagesForward = () => {
    if (galleryOffset + 1 > getMaximumOffset()) return null

    setGalleryOffset(galleryOffset + 1)
  }

  const handleImagesBackward = () => {
    if (galleryOffset - 1 < 0) return null

    setGalleryOffset(galleryOffset - 1)
  }

  const showGallery = props.photos.length > 0
  const showControls = props.photos.length > 1

  const imageIsVertical = dimensions && (dimensions.height / dimensions.width) > 1

  const targetContainer = document.getElementById('booqable-quickview-inner') || document.body

  return createPortal(
    <div className="booqable-product-gallery-wrapper" data-tid="Product gallery">
      <BModalGallery>
        <BModalGallery.Wrapper>
          <BModalGallery.Header>
            <Icon icon="arrow-left" onClick={props.onClose} hover />
            <BModalGallery.Title>{props.itemTitle}</BModalGallery.Title>
          </BModalGallery.Header>
          <BModalGallery.Body
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <BModalGallery.Image
              src={props.selectedPhoto.xlarge_url}
              onLoad={onImageLoad}
              width={imageIsVertical ? 'auto' : '100%'}
              height={imageIsVertical ? '100%' : 'auto'}
            />
            {showControls && (
              <BModalGallery.Controls>
                <BModalGallery.ControlWrapper
                  onClick={() => {
                    props.handlePhotoSelect(-1)
                    handleImagesBackward()
                  }}
                >
                  <Icon icon="chevron-left" />
                </BModalGallery.ControlWrapper>
                <BModalGallery.ControlWrapper
                  onClick={() => {
                    props.handlePhotoSelect(1)
                    handleImagesForward()
                  }}
                >
                  <Icon icon="chevron-right" />
                </BModalGallery.ControlWrapper>
              </BModalGallery.Controls>
            )}
          </BModalGallery.Body>
          <BModalGallery.Footer>
            {showGallery && (
              <BModalGallery.Gallery size={THUMBNAIL_SIZE} offset={galleryOffset}>
                <BModalGallery.GalleryImagesContainer>
                  <BModalGallery.GalleryImages>
                    {props.photos.map((photo) => (
                      <BModalGallery.GalleryImageContainer
                        key={photo.id}
                        selected={
                          props.selectedPhoto &&
                          props.selectedPhoto.id === photo.id
                        }
                        brandingColor={props.brandingColor}
                        onClick={() => props.onThumbnailPress(photo)}
                      >
                        <FocalImage
                          url={photo.large_url}
                          preview={_get(photo, 'preview')}
                          coordinates={photo.coordinates}
                          size={`${THUMBNAIL_SIZE}px`}
                          noBorder
                          noBorderRadius
                        />
                      </BModalGallery.GalleryImageContainer>
                    ))}
                  </BModalGallery.GalleryImages>
                </BModalGallery.GalleryImagesContainer>
              </BModalGallery.Gallery>
            )}
          </BModalGallery.Footer>
        </BModalGallery.Wrapper>
      </BModalGallery>
    </div>,
    targetContainer
  )
}

export default ModalGallery
